// The object of a statement, asked the way the property's declaration says:
// a thing picked by name, or a value in the box its kind needs (a date
// picker, a number field, text with a language menu). Where nothing is
// declared, the person says which it is, in plain names (77.7).
import { useAuth } from 'react-oidc-context';
import { EntityPicker } from '@/components/EntityPicker';
import { ALL_GRAPHS } from '@/components/GraphSelector';
import { fieldClass } from '@/components/ui/field';
import { SUPPORTED_LANGUAGES } from '@/lib/preferences';
import { useOntologySchema } from '@/lib/useOntology';
import type { UriLabel } from '@/lib/useEntity';
import {
  EMPTY_LITERAL,
  EMPTY_THING,
  VALUE_TYPES,
  valueShape,
  type ObjState,
  type ValueShape,
  type ValueType,
} from '@/lib/statementValue';

const RANGE_WORDS: Record<ValueType, string> = {
  text: 'text',
  number: 'a number',
  whole: 'a whole number',
  date: 'a date',
  datetime: 'a date and time',
  time: 'a time of day',
  yesno: 'yes or no',
  web: 'a web address',
};

function typeOfDatatype(datatype: string): ValueType {
  return VALUE_TYPES.find((t) => t.datatype === datatype)?.type ?? 'text';
}

/** The declaration of `predicate` across every graph, when one is held. */
function useShape(predicate: UriLabel | null): ValueShape | null {
  const auth = useAuth();
  const schema = useOntologySchema(ALL_GRAPHS, auth.user?.id_token);
  if (!predicate) return null;
  const declared = schema.data?.properties.find((p) => p.uri === predicate.uri);
  return valueShape(predicate.uri, declared);
}

function ValueBox({
  type,
  state,
  onChange,
}: {
  type: ValueType;
  state: Extract<ObjState, { mode: 'literal' }>;
  onChange: (s: ObjState) => void;
}) {
  const set = (value: string) => onChange({ ...state, value });
  const box = fieldClass('normal', 'w-full py-1');
  switch (type) {
    case 'date':
      return <input type="date" aria-label="Value" className={box} value={state.value} onChange={(e) => set(e.target.value)} />;
    case 'datetime':
      return (
        <input type="datetime-local" aria-label="Value" className={box} value={state.value.slice(0, 16)} onChange={(e) => set(e.target.value)} />
      );
    case 'time':
      return <input type="time" aria-label="Value" className={box} value={state.value.slice(0, 5)} onChange={(e) => set(e.target.value)} />;
    case 'number':
    case 'whole':
      return (
        <input
          type="number"
          aria-label="Value"
          step={type === 'whole' ? 1 : 'any'}
          className={box}
          value={state.value}
          onChange={(e) => set(e.target.value)}
        />
      );
    case 'yesno':
      return (
        <select aria-label="Value" className={box} value={state.value} onChange={(e) => set(e.target.value)}>
          <option value="">Choose</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    case 'web':
      return (
        <input type="url" aria-label="Value" placeholder="https://…" className={box} value={state.value} onChange={(e) => set(e.target.value)} />
      );
    case 'text':
      return (
        <div className="flex gap-2">
          <input
            type="text"
            aria-label="Value"
            className={fieldClass('normal', 'min-w-0 flex-1 py-1')}
            value={state.value}
            onChange={(e) => set(e.target.value)}
          />
          <select
            aria-label="Language"
            className={fieldClass('normal', 'w-36 py-1')}
            value={state.lang}
            onChange={(e) => onChange({ ...state, lang: e.target.value })}
          >
            <option value="">No language</option>
            {SUPPORTED_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
        </div>
      );
  }
}

/** A value of `type`, starting from whatever was entered before. */
function asLiteral(state: ObjState, type: ValueType, datatype: string | null): Extract<ObjState, { mode: 'literal' }> {
  const base = state.mode === 'literal' ? state : EMPTY_LITERAL;
  return {
    ...base,
    datatype: datatype ?? '',
    lang: type === 'text' ? base.lang : '',
  };
}

export function StatementValueInput({
  predicate,
  state,
  onChange,
}: {
  predicate: UriLabel | null;
  state: ObjState;
  onChange: (s: ObjState) => void;
}) {
  const shape = useShape(predicate);
  if (!shape) {
    return <p className="py-1 text-xs text-tres-text-secondary">Pick the property first.</p>;
  }

  if (shape.kind === 'thing') {
    return (
      <div className="space-y-1">
        <EntityPicker
          selected={state.mode === 'uri' ? state.sel : null}
          onSelect={(sel) => onChange({ mode: 'uri', sel })}
          placeholder={shape.pick === 'class' ? 'Search for the class by name…' : 'Search for the thing by name…'}
          label="The thing it points to"
          {...(shape.pick ? { kind: shape.pick } : {})}
        />
        <p className="text-xs text-tres-text-secondary">
          {shape.pick === 'class' ? 'This property names a class.' : 'This property points to a thing.'}
        </p>
      </div>
    );
  }

  if (shape.kind === 'value') {
    const literal = asLiteral(state, shape.type, shape.datatype);
    return (
      <div className="space-y-1">
        <ValueBox type={shape.type} state={literal} onChange={(s) => onChange(s.mode === 'literal' ? { ...s, datatype: shape.datatype ?? '' } : s)} />
        <p className="text-xs text-tres-text-secondary">This property holds {RANGE_WORDS[shape.type]}.</p>
      </div>
    );
  }

  const isThing = state.mode === 'uri';
  const type = state.mode === 'literal' ? typeOfDatatype(state.datatype) : 'text';
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="What it holds"
          className={fieldClass('normal', 'py-1')}
          value={isThing ? 'thing' : type}
          onChange={(e) => {
            const next = e.target.value;
            if (next === 'thing') {
              onChange(EMPTY_THING);
              return;
            }
            const t = VALUE_TYPES.find((v) => v.type === next) ?? VALUE_TYPES[0];
            if (t) onChange(asLiteral(EMPTY_LITERAL, t.type, t.datatype));
          }}
        >
          <option value="thing">A thing</option>
          {VALUE_TYPES.map((t) => (
            <option key={t.type} value={t.type}>
              {t.name}
            </option>
          ))}
        </select>
      </div>
      {state.mode === 'uri' ? (
        <EntityPicker
          selected={state.sel}
          onSelect={(sel) => onChange({ mode: 'uri', sel })}
          placeholder="Search for the thing by name…"
          label="The thing it points to"
        />
      ) : (
        <ValueBox type={type} state={state} onChange={onChange} />
      )}
      <p className="text-xs text-tres-text-secondary">
        The ontology does not say what this property holds; choose a thing or the kind of value.
      </p>
    </div>
  );
}
